/**
 * Date-only utility functions for handling dates without time/timezone concerns.
 *
 * These utilities ensure that dates are parsed and formatted in local time,
 * avoiding the common UTC parsing bug where YYYY-MM-DD strings are interpreted
 * as UTC midnight and can shift by one day depending on timezone.
 */

/**
 * Parses a date-only ISO string into a local Date object at midnight.
 *
 * Accepts dates in YYYY-MM-DD format, with optional zero-padding for month and day
 * (e.g., both "2024-01-05" and "2024-1-5" are valid).
 *
 * Validates the input format and throws a clear error for invalid inputs to prevent
 * silent bugs from propagating NaN dates throughout the application.
 *
 * @param isoString - Date string in YYYY-M-D or YYYY-MM-DD format
 * @returns Date object representing local midnight on the specified date
 * @throws {Error} If the input is not a valid date string
 *
 * @example
 * const date1 = parseDateOnly('2024-12-25'); // Dec 25, 2024 at 00:00 local time
 * const date2 = parseDateOnly('2024-1-5');   // Jan 5, 2024 at 00:00 local time
 * parseDateOnly('invalid'); // throws Error
 */
export function parseDateOnly(isoString: string): Date {
  // Validate input is a string
  if (typeof isoString !== 'string') {
    throw new Error(`parseDateOnly: expected string, got ${typeof isoString}`);
  }

  // Split and parse components
  const parts = isoString.split('-');
  if (parts.length !== 3) {
    throw new Error(
      `parseDateOnly: expected format YYYY-M-D or YYYY-MM-DD, got "${isoString}" (${parts.length} parts)`,
    );
  }

  const [yearStr, monthStr, dayStr] = parts;
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);

  // Validate all parts are finite numbers
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    throw new Error(
      `parseDateOnly: non-numeric components in "${isoString}" (year=${yearStr}, month=${monthStr}, day=${dayStr})`,
    );
  }

  // Validate ranges (basic sanity checks)
  if (month < 1 || month > 12) {
    throw new Error(`parseDateOnly: month must be 1-12, got ${month} in "${isoString}"`);
  }

  if (day < 1 || day > 31) {
    throw new Error(`parseDateOnly: day must be 1-31, got ${day} in "${isoString}"`);
  }

  // Create the date
  const date = new Date(year, month - 1, day);

  // Validate the resulting date isn't Invalid
  if (isNaN(date.getTime())) {
    throw new Error(`parseDateOnly: resulted in Invalid Date for "${isoString}"`);
  }

  // Validate that the date wasn't normalized (e.g., Feb 31 → Mar 3)
  // This prevents silently accepting impossible dates like "2024-02-31"
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    throw new Error(
      `parseDateOnly: invalid date "${isoString}" (would normalize to ${formatDateOnly(date)})`,
    );
  }

  return date;
}

/**
 * Formats a Date object as a date-only ISO string (YYYY-MM-DD) using local time.
 *
 * @param date - Date object to format
 * @returns Date string in YYYY-MM-DD format
 *
 * @example
 * const isoString = formatDateOnly(new Date(2024, 11, 25)); // "2024-12-25"
 */
export function formatDateOnly(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Compares two dates ignoring time components (date-only comparison).
 *
 * @param date1 - First date to compare
 * @param date2 - Second date to compare
 * @returns True if dates represent the same day (ignoring time)
 *
 * @example
 * const isSameDay = areSameDateOnly(
 *   new Date(2024, 11, 25, 10, 30),
 *   new Date(2024, 11, 25, 18, 45)
 * ); // true
 */
export function areSameDateOnly(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}
